import { useEffect, useMemo, useState } from "react";

import { DateTime } from "luxon";

import { useQueryClient } from "@tanstack/react-query";

import CODStatus from "./CODStatus";
import Card from "../../shared/Card/Card";
import Modal from "../../../layouts/Modal";
import useEmails from "../../../data/useEmails";
import { IVehicle } from "../../../models/Vehicle";
import useSingleLead from "../../../data/useSingleLead";
import ContactFromId from "../../contacts/ContactFromId";
import DropOffPlannerView from "../../dropOffPlanner/DropOffPlannerView";
import formatDateTime from "../../../utils/formatDateTime";
import ConfirmModal from "../../shared/Confirm/ConfirmModal";
import TripManager from "../../../pages/Calendar/TripManager";
import { ButtonInput } from "../../shared/Inputs/ButtonInput";
import useNotification from "../../notifications/useNotifications";
import { ICompleteLeadResponse, ILead, LeadSource, LeadStatus } from "../../../models/Lead";
import useVehiclesMutations from "../../../data/useVehiclesMutations";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import useLeads, { collectFailureText } from "../../../data/useLeads";
import useSav from "../../../data/useSav";
import { useAuth } from "../../../contexts/AuthContext";
import { wilmaSAVCustomerId } from "../../../config";
import SavCustomerPickerPill from "../LeadTable/SavCustomerPickerPill";

export default function LeadStatusCard({ leadId }: { leadId: string }) {
  const { customerId, userId } = useAuth();
  const { lead, update: updateLead, complete, downloadDoc, getCollectionNote } = useSingleLead(leadId, "LeadStatusCard");
  const isSavCustomer = !!customerId && customerId === wilmaSAVCustomerId;
  const { customerNames, allocateAtf } = useSav(
    isSavCustomer ? customerId : undefined,
    !isSavCustomer,
    "LeadStatusCard"
  );
  const { undropLead, toggleLeadCollectionDropOff, markLeadCollected } = useLeads(true, "LeadStatusCard");
  const { update: updateVehicle } = useVehiclesMutations();
  const queryClient = useQueryClient();

  const { sendDocumentsEmail } = useEmails();
  const { addNotification } = useNotification();
  const { saveHandlers } = useDefaultCRUDHandlers("Lead");
  const [collectionOnOpen, setCollectionOnOpen] = useState(false);
  const [dropOffOpen, setDropOffOpen] = useState(false);

  const [confirmComplete, setConfirmComplete] = useState(false);
  const [toggleCollectionOpen, setToggleCollectionOpen] = useState(false);
  const [collectedOpen, setCollectedOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);
  const [completeLoading, setCompleteLoading] = useState(false);
  const [collectionOn, setCollectionOn] = useState<string | null>(null);
  const [collectionOnSlot, setCollectionOnSlot] = useState<number>(0);
  const [collectedOn, setCollectedOn] = useState<string | null>(null);
  const [leadStatus, setLeadStatus] = useState(LeadStatus["Awaiting Contact"]);
  const [targetCustomerId, setTargetCustomerId] = useState<string>("");
  const [allocateConfirmOpen, setAllocateConfirmOpen] = useState(false);
  const allocatedCustomerName = customerNames.data?.data?.find(x => x.id === lead.data?.data?.allocatedToCustomerId)?.name;
  const isAllocated = !!lead.data?.data?.allocatedToCustomerId;


  const isDropOff = useMemo(() => lead.data?.data.vehicle?.isDropOff, [lead.data])


  const handleSaveCollectionOn = () => {
    const updatedVehicle: IVehicle = {
      ...lead.data?.data.vehicle!,
      scheduledCollectionOn: collectionOn,
      scheduledCollectionSlot: collectionOnSlot
    };

    setLeadStatus(LeadStatus["Scheduled"]);
    handleUpdateVehicle(updatedVehicle);
  }

  const handleSaveCollectedOn = () => {
    const collected = lead.data?.data.vehicle?.scheduledCollectionOn ? DateTime.fromISO(lead.data?.data.vehicle?.scheduledCollectionOn) : DateTime.now();

    setCollectedOn(collected.toISODate());
    setLeadStatus(LeadStatus["Collected"]);

    // Send the lead, not the vehicle. One Vehicle row is shared by every lead on the
    // registration, so a vehicle-level update leaves the API to infer which quote was
    // honoured — which could stamp an old or cancelled lead as collected and push it into
    // the payments queue. This endpoint sets the lead's status and the vehicle's collection
    // fields together, and reports the right CCID to CarTakeBack.
    markLeadCollected.mutate(
      { leadId: lead.data?.data.id ?? "", collectedOn: collected.toISODate() },
      {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setCollectedOpen(false);

          //Need to wait for the query to update before invalidating lead - somehow the best way to make this work
          setTimeout(() => {
            queryClient.invalidateQueries(["lead", leadId]);
          }, 1000 * 2);
        },
        onError: (error) => {
          // Show the API's reason rather than a generic failure — see collectFailureText.
          addNotification({ variant: "error", primaryText: collectFailureText(error) });
        }
      }
    );
  }

  const handleClearCollectionOn = () => {
    const updatedVehicle: IVehicle = {
      ...lead.data?.data.vehicle!,
      scheduledCollectionOn: null,
      scheduledCollectionSlot: 0,
      collectionStart: null,
      collectionEnd: null,
    };

    const updatedLead: ILead = {
      ...lead.data?.data!,
      vehicle: updatedVehicle,
      status: LeadStatus["Awaiting Contact"]
    }

    setLeadStatus(LeadStatus["Awaiting Contact"]);

    setCollectionOn(null);
    setCollectionOnSlot(0);

    undropLead.mutate(lead.data?.data.id ?? "", {
      onSuccess: () => {
        handleUpdateVehicle(updatedVehicle);
      },
      onError: () => {
        saveHandlers.onError();
      }
    });
  }

  const handleClearCollectedOn = () => {
    const updatedVehicle: IVehicle = {
      ...lead.data?.data.vehicle!,
      collectedOn: null
    };

    setCollectedOn("");

    if (updatedVehicle.scheduledCollectionOn) {
      setLeadStatus(LeadStatus["Scheduled"]);
    } else {
      setLeadStatus(LeadStatus["Awaiting Contact"]);
    }

    handleUpdateVehicle(updatedVehicle);
  }

  const handleUpdateVehicle = async (vehicle: IVehicle) => {
    await updateVehicle.mutateAsync(vehicle, {
      onSuccess: () => {
        saveHandlers.onSuccess();
        setCollectionOnOpen(false);
        setCollectedOpen(false);

        //Need to wait for the query to update before invalidating lead - somehow the best way to make this work
        setTimeout(() => {
          queryClient.invalidateQueries(["lead", leadId]);
        }, 1000 * 2);
      },
      onError: () => {
        saveHandlers.onError();
      }
    })
  }

  const handleStatusChange = (status: string) => {
    const newStatus = parseInt(status);

    const updatedLead = {
      ...lead.data?.data!,
      status: newStatus
    };

    setLeadStatus(newStatus);

    // Changing to Collected goes through the lead-level collect endpoint, which sets this
    // lead's status AND the vehicle's collected date in one write. It used to save the lead
    // and then PUT the vehicle as a second call, and that second call stamped a second,
    // arbitrarily chosen lead on the same registration as collected too — the duplicate rows
    // in the Vehicles Collected table. Only do this when the vehicle isn't already collected,
    // so re-picking Collected can't overwrite the original collection date and collector.
    if (newStatus === LeadStatus["Collected"] && !lead.data?.data.vehicle?.collectedOn) {
      handleSaveCollectedOn();
      return;
    }

    updateLead.mutate(updatedLead, saveHandlers);
  }

  const printCodCertificate = () => {
    var ids = {
      leadId: lead.data?.data.id,
      docId: lead.data?.data.vehicle?.codCertificateDocument
    };

    downloadDoc.mutate(ids, {
      onSuccess: (file: any) => {
        const url = window.URL.createObjectURL(file);
        const w = window.open(url, "_blank");
        w?.print();
      }
    });
  }

  const sendDocuments = (collectionNote: boolean, codCertificate: boolean) => {
    let body = {
      leadId: lead.data?.data.id,
      collectionNote: collectionNote,
      codCertificate: codCertificate
    }

    sendDocumentsEmail.mutate(body, {
      onSuccess: () => {
        addNotification({
          variant: "success",
          primaryText: "Documents sent",
        });
      },
      onError: () => {
        addNotification({
          variant: "error",
          primaryText: "Unable to send documents",
        });
      }
    });
  }

  const printCollectionNote = (createNew: boolean) => {
    getCollectionNote.mutate({ leadId: lead.data?.data.id!, createNew: createNew }, {
      onSuccess: (file: any) => {
        //print file
        const url = window.URL.createObjectURL(file);
        const w = window.open(url, "_blank");
        w?.print();
      },
      onError: () => {
        addNotification({
          variant: "error",
          primaryText: "Unable to print collection note",
        });
      }
    })
  }

  const handleComplete = () => {
    setCompleteLoading(true);

    complete.mutate(lead.data?.data.id!, {
      onSuccess: (res: ICompleteLeadResponse) => {
        setConfirmComplete(false);
        // The lead completed either way; the message says why the invoice didn't, and what to fix.
        addNotification({
          variant: res.invoiceCreated === false ? "warn" : "success",
          primaryText: res.message,
        })
      },
      onError: (res: any) => {
        addNotification({
          variant: "error",
          primaryText: res.message,
        })
      },
      onSettled: () => {
        setCompleteLoading(false);
      }
    })
  }

  const handleToggleCollectionDropOff = () => {
    //call toggle endpoint
    toggleLeadCollectionDropOff.mutate(lead.data?.data.id!, {
      ...saveHandlers,
      onSuccess: () => {
        saveHandlers.onSuccess();
        setToggleCollectionOpen(false);
      }
    });
  }

  const handleAllocateLead = () => {
    if (!lead.data?.data?.id || !targetCustomerId) return;

    allocateAtf.mutate(
      { leadId: lead.data.data.id, targetCustomerId },
      {
        onSuccess: () => {
          setAllocateConfirmOpen(false);
          addNotification({
            variant: "success",
            primaryText: "Lead allocated successfully",
          });
        },
        onError: (err: any) => {
          addNotification({
            variant: "error",
            primaryText: err?.message ?? "Failed to allocate lead",
          });
        },
      }
    );
  };

  const handleOpenCancel = () => {
    setCancelReason(lead.data?.data?.cancelledReason ?? "");
    setCancelOpen(true);
  };

  const handleCancelLead = () => {
    const currentLead = lead.data?.data;
    if (!currentLead) return;

    const reason = cancelReason.trim();
    if (!reason) {
      addNotification({
        variant: "error",
        primaryText: "Please provide a cancellation reason.",
      });
      return;
    }

    setCancelLoading(true);
    updateLead.mutate(
      {
        ...currentLead,
        status: LeadStatus.Cancelled,
        cancelledReason: reason,
        cancelledBy: userId ?? null,
        cancelledOn: new Date().toISOString(),
      },
      {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setCancelOpen(false);
        },
        onError: () => {
          saveHandlers.onError();
        },
        onSettled: () => {
          setCancelLoading(false);
        },
      }
    );
  };

  const LeadStatusRow = ({ label, value }: { label: string, value: string }) => (
    <div className="py-2 grid grid-cols-2 gap-4">
      <dt className="text-sm font-medium text-gray-500">
        {label}
      </dt>
      <dd className="items-center text-sm text-gray-900">
        <div>
          {value}
        </div>
      </dd>
    </div>
  );

  const formatCollectionDate = (dateValue?: string | null) => {
    if (!dateValue) return "Not Set";
    return formatDateTime(
      dateValue,
      dateValue.includes("T") ? DateTime.DATETIME_SHORT : DateTime.DATE_SHORT
    );
  };

  useEffect(() => {
    if (lead.isLoading || !lead.data?.data) return;

    // console.log(lead.data.data)
    setLeadStatus(lead.data.data.status);
    setCollectionOn(lead.data.data.vehicle?.scheduledCollectionOn ?? "");
    setCollectionOnSlot(lead.data.data.vehicle?.scheduledCollectionSlot ?? 0);
    setCollectedOn(lead.data.data.vehicle?.collectedOn ?? "");
    setLeadStatus(lead.data.data.status);

  }, [lead.isLoading, lead.data?.data])

  useEffect(() => {
    if (!isSavCustomer || !customerNames.data?.data?.length || targetCustomerId) return;
    const defaultCustomerId = customerNames.data.data.some(x => x.id === customerId)
      ? customerId
      : customerNames.data.data[0].id;
    setTargetCustomerId(defaultCustomerId ?? "");
  }, [isSavCustomer, customerNames.data, targetCustomerId, customerId]);

  return (
    <>
      <ConfirmModal open={allocateConfirmOpen} setOpen={setAllocateConfirmOpen} onConfirm={() => handleAllocateLead()}
        title="Allocate Booking"
        message={`Are you sure you want to allocate this booking to ${customerNames.data?.data?.find(x => x.id === targetCustomerId)?.name ?? "another customer"}?\n\nThis action is permanent and cannot be undone.`}
        confirmButtonText="Confirm" isLoading={allocateAtf.isLoading}
      />
      <ConfirmModal open={confirmComplete} setOpen={setConfirmComplete} onConfirm={() => handleComplete()}
        title="Complete Lead" message={`Really complete this lead?\nThis will update the status to 'Complete' and send the customer a confirmation email.`}
        confirmButtonText="Complete" isLoading={completeLoading}
      />
      <ConfirmModal open={toggleCollectionOpen} setOpen={setToggleCollectionOpen} onConfirm={() => handleToggleCollectionDropOff()}
        title="Toggle Collection/Drop Off" message={`Really change this lead type?\nThis will update the status to '${isDropOff ? "Collection" : "Drop Off"}'.\nAny scheduled ${isDropOff ? "drop off" : "collection"} date will be cleared and lead will revert to Awaiting Contact.`}
        confirmButtonText="Toggle" isLoading={completeLoading}
      />
      <Modal open={collectionOnOpen} setOpen={setCollectionOnOpen} width="max-w-7xl">
        <div className="flex justify-between">
          <label>Set Collection Scheduled On:</label>

          <button
            onClick={() => handleClearCollectionOn()}
            className="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
          >
            Clear
          </button>
        </div>

        {/* <Calendar vrm={lead.data?.data.vehicle?.vrm} editable={false} waiting={false} /> */}
        <TripManager />

      </Modal>

      <Modal open={dropOffOpen} setOpen={setDropOffOpen} width="max-w-7xl">
        <DropOffPlannerView
          currentLeadId={lead.data?.data?.id}
          containerClassName="flex flex-col gap-2 h-[75vh]"
        />
      </Modal>
      <Modal open={cancelOpen} setOpen={setCancelOpen} width="max-w-2xl">
        <div className="space-y-4">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Cancel Lead</h3>
          <p className="text-sm text-gray-500">Please enter a reason for cancelling this lead.</p>
          <textarea
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            rows={4}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Enter cancellation reason..."
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
              onClick={() => setCancelOpen(false)}
              disabled={cancelLoading}
            >
              Close
            </button>
            <button
              type="button"
              className="rounded-md border border-transparent bg-red-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 disabled:opacity-60"
              onClick={handleCancelLead}
              disabled={cancelLoading}
            >
              {cancelLoading ? "Cancelling..." : "Cancel Lead"}
            </button>
          </div>
        </div>
      </Modal>

      <Card
        bodyClassName="p-4"
        title="Lead Status"
        collapsible
      >
        <div className="md:grid md:grid-cols-5 gap-3">

          <div className="col-span-5 divide-y ">
            <div className="py-2 grid grid-cols-2 gap-4 items-center">
              <dt className="text-sm font-medium text-gray-500">
                Lead Type
              </dt>
              <dd className="flex justify-between items-center text-sm text-gray-900">
                <div>
                  {isDropOff ? "Drop Off" : "Collection"}
                </div>

                <button
                  type="button"
                  onClick={() => setToggleCollectionOpen(true)}
                  className="ml-3 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-1 py-1 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700">
                  Change to {isDropOff ? "Collection" : "Drop Off"}
                </button>
              </dd>
            </div>

            <div className="py-2 grid grid-cols-2 gap-4 items-center">
              <dt className="text-sm font-medium text-gray-500">
                Lead Source
              </dt>
              <dd className="items-center text-sm text-gray-900">
                <div>
                  {LeadSource[lead.data?.data.website!]}
                </div>
              </dd>
            </div>
            {lead.data?.data.preferredCollectionDate && (
              <div className="py-2 grid grid-cols-2 gap-4 items-center">
                <dt className="text-sm font-medium text-gray-500">
                  Preferred Collection Date
                </dt>
                <dd className="items-center text-sm text-gray-900">
                  {formatDateTime(lead.data.data.preferredCollectionDate, DateTime.DATE_SHORT)}
                </dd>
              </div>
            )}
            <div className="py-2 grid grid-cols-2 gap-4 items-center">
              <dt className="text-sm font-medium text-gray-500">
                {isDropOff ? "Drop Off" : "Collection"} Scheduled On
              </dt>
              <dd className="flex justify-between items-center text-sm text-gray-900">
                <div>
                  {collectionOn ? formatDateTime(collectionOn, DateTime.DATE_SHORT) : "Not Set"}
                </div>
                <button
                  type="button"
                  onClick={() => { isDropOff ? setDropOffOpen(true) : setCollectionOnOpen(true) }}
                  className="ml-3 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-1 py-1 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700">
                  Change
                </button>
              </dd>
            </div>
            <div className="py-2 grid grid-cols-2 gap-4 items-center">
              <dt className="text-sm font-medium text-gray-500">
                {isDropOff ? "Dropped Off" : "Collected"} On
              </dt>
              <dd className="flex justify-between items-center text-sm text-gray-900">
                <div className="flex flex-col gap-y-1">
                  <span>{formatCollectionDate(collectedOn)}</span>
                </div>
                {collectedOn ? (
                  <button
                    type="button"
                    onClick={() => handleClearCollectedOn()}
                    className="ml-3 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-1 py-1 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700">
                    Clear
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleSaveCollectedOn()}
                    className="ml-3 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-1 py-1 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700">
                    Set as {isDropOff ? "Dropped Off" : "Collected"}
                  </button>
                )}
              </dd>
            </div>
            {collectedOn && (
              <div className="py-2 grid grid-cols-2 gap-4 items-center">
                <dt className="text-sm font-medium text-gray-500">
                  Collected By
                </dt>
                <dd className="items-center text-sm text-gray-900">
                  <div>
                    {lead.data?.data.vehicle?.collectedBy ? <ContactFromId contactId={lead.data?.data.vehicle?.collectedBy} /> : "Unknown"}
                  </div>
                </dd>
              </div>
            )}

            <CODStatus leadId={leadId} />

            {lead.data?.data.status !== LeadStatus.Cancelled && (
              <div className="py-2 grid grid-cols-2 gap-4 items-center">
                <dt className="flex items-center text-sm font-medium text-gray-500">
                  Status
                </dt>
                <dd className="text-sm text-gray-900">
                  <select
                    // defaultValue={lead.status}
                    value={leadStatus}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    className="border border-gray-400 rounded-md text-sm"
                  >
                    {Object.values(LeadStatus)
                      .filter((ct) => isNaN(Number(ct)))
                      .filter((status) => { return status !== "Cancelled"; }) //Use cancelled button instead
                      //Only SAV hands leads to another yard, so only SAV can set Allocated
                      .filter((status) => isSavCustomer || status !== "Allocated")
                      .map((status) => (
                        <option key={status} value={LeadStatus[status as keyof typeof LeadStatus]}>
                          {status}
                        </option>
                      ))}
                  </select>
                </dd>
              </div>
            )}



            {lead.data?.data.cancelledBy && (
              <div className="py-2 grid grid-cols-2 gap-4 items-center">
                <dt className="flex items-center text-sm font-medium text-gray-500">
                  Cancelled By
                </dt>
                <dd className="text-sm text-gray-900">
                  {lead.data?.data.cancelledBy ? <ContactFromId contactId={lead.data?.data.cancelledBy} /> : "N/A"}
                </dd>
              </div>
            )}

            {lead.data?.data.cancelledReason && (
              <div className="py-2 grid grid-cols-2 gap-4 items-center">
                <dt className="flex items-center text-sm font-medium text-gray-500">
                  Cancelled Reason
                </dt>
                <dd className="text-sm text-gray-900">
                  {lead.data?.data.cancelledReason || "N/A"}
                </dd>
              </div>
            )}

            {lead.data?.data.status === LeadStatus.Cancelled && (
              <div className="py-2 grid grid-cols-2 gap-4 items-center">
                <dt className="flex items-center text-sm font-medium text-gray-500">
                  
                </dt>
                <dd className="text-sm text-gray-900">
                  <ButtonInput
                    label="Reinstate Lead"
                    classes="bg-emerald-600 hover:bg-emerald-700"
                    isSubmit={false}
                    onClick={() => handleStatusChange(LeadStatus["Awaiting Contact"].toString())}
                  />
                </dd>
              </div>
            )}

            {isSavCustomer && (
              <div className="py-2 grid grid-cols-2 gap-4 items-center">
                <dt className="text-sm font-medium text-gray-500">
                  Allocate To
                </dt>
                <dd className="flex items-center justify-between gap-2 text-sm text-gray-900">
                  {/* if allocated and allocated to id isn't sav */}
                  {isAllocated && lead.data?.data?.allocatedToCustomerId !== wilmaSAVCustomerId ? (
                    <div className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 px-3 py-1 text-sm font-semibold">
                      Allocated to {allocatedCustomerName || "-"}
                    </div>
                  ) : (
                    <>
                      <SavCustomerPickerPill
                        value={targetCustomerId || null}
                        customers={customerNames.data?.data ?? []}
                        onChange={(id) => setTargetCustomerId(id ?? "")}
                        placeholder="Assign customer"
                        disabled={customerNames.isLoading}
                      />
                      <button
                        type="button"
                        disabled={!targetCustomerId || allocateAtf.isLoading}
                        onClick={() => setAllocateConfirmOpen(true)}
                        className="inline-flex items-center rounded-md border border-transparent bg-primary-600 px-2 py-1 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700 disabled:opacity-50"
                      >
                        {allocateAtf.isLoading ? "Allocating..." : "Allocate"}
                      </button>
                    </>
                  )}
                </dd>
              </div>
            )}

            <div className="pt-3 flex justify-between items-center">
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-x-3 gap-y-3">

                <ButtonInput label="Print Collection Note" classes="" isSubmit={false} onClick={() => printCollectionNote(true)} />

                {lead.data?.data.vehicle?.codCertificateDocument ? (
                  <ButtonInput label="Print COD" classes="" isSubmit={false} onClick={() => printCodCertificate()} />
                ) : (
                  <div> {/* spacer */} </div>
                )}

                <div></div>

                <ButtonInput label="Send Collection Note" classes="" isSubmit={false} onClick={() => sendDocuments(true, false)} />
                {lead.data?.data.vehicle?.codCertificateNumber && (
                  <>
                    <ButtonInput label="Send COD Certificate" classes="" isSubmit={false} onClick={() => sendDocuments(false, true)} />
                    <ButtonInput label="Send Both" classes="" isSubmit={false} onClick={() => sendDocuments(true, true)} />
                  </>
                )}

              </div>
              <div className="flex gap-2">
                <ButtonInput
                  label="Cancel Lead"
                  classes="bg-amber-600 hover:bg-amber-700"
                  isSubmit={false}
                  onClick={handleOpenCancel}
                />
                <ButtonInput label="Complete Lead" classes={lead.data?.data.completedOn && "bg-green-600 hover:bg-green-700"}
                  isSubmit={false} onClick={() => setConfirmComplete(true)}
                />
              </div>
            </div>
          </div>

        </div>
      </Card >
    </>
  );
}
